import React, { useEffect, useState } from "react";
import apiClient from "data/apiClient";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled, { css } from "styled-components/macro";
import { SectionHeading } from "components/misc/Headings";
import { PrimaryButton } from "components/misc/Buttons";
import WordSpacedText from "../components/WordSpacedText";
import { getAbsoluteUrl } from "utils/urlUtils";

const HeadingRow = tw.div`flex`;
const Heading = tw(SectionHeading)`text-gray-900`;
const Posts = tw.div`mt-6 sm:-mr-8 flex flex-wrap`;

const PostContainer = styled.div`
  ${tw`mt-10 w-full sm:w-1/2 lg:w-1/3 sm:pr-8`}
  ${(props) =>
    props.featured &&
    css`
      ${tw`w-full!`}
      ${Post} {
        ${tw`sm:flex-row! h-full sm:pr-4`}
      }
      ${Image} {
        ${tw`sm:h-96 sm:min-h-full sm:w-1/2 lg:w-2/3 sm:rounded-t-none sm:rounded-l-lg`}
      }
      ${Info} {
        ${tw`sm:-mr-4 sm:pl-8 sm:flex-1 sm:rounded-none sm:rounded-r-lg sm:border-t-2 sm:border-l-0`}
      }
      ${Description} {
        ${tw`text-sm mt-3 leading-loose text-gray-600 font-medium`}
      }
    `}
`;

const Post = tw.div`cursor-pointer flex flex-col bg-gray-100 rounded-lg`;

const Image = styled.div`
  ${(props) =>
    css`
      background-image: url("${props.imageSrc}");
    `}
  ${tw`h-64 w-full bg-cover bg-center rounded-t-lg`}
`;

const Info = tw.div`p-8 border-2 border-t-0 rounded-lg rounded-t-none`;
const Category = tw.div`uppercase text-primary-500 text-xs font-bold tracking-widest leading-loose after:content after:block after:border-b-2 after:border-primary-500 after:w-8`;
const CreationDate = tw.div`mt-4 uppercase text-gray-600 italic font-semibold text-xs`;
const Title = tw.div`mt-1 font-black text-sm lg:text-xl text-gray-900 group-hover:text-primary-500 transition duration-300`;
const Description = tw.div``;

const ButtonContainer = tw.div`flex justify-center`;
const LoadMoreButton = tw(PrimaryButton)`mt-16 mx-auto`;

const PAGE_SIZE = 6;
const API_BASE_URL =
  (apiClient && apiClient.defaults && apiClient.defaults.baseURL) ||
  process.env.REACT_APP_BASE_URL ||
  "";

export default function FacultyDawahSection({
  headingText = "Faculty Dawah Activities.",
}) {
  const [posts, setPosts] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // Safely resolve an image URL from varying API shapes and normalize to absolute
  const resolveImageSrc = (post) => {
    console.log(post.ImageUrl);
    const raw =
      (post &&
        (post.imageUrl || post.imageURL || post.image_path || post.image)) ||
      "";
    if (!raw || typeof raw !== "string") {
      // Fallback placeholder if API item has no usable image
      return "";
    }
    return getAbsoluteUrl(raw);
  };

  useEffect(() => {
    fetchFacultyDawah(pageNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  const fetchFacultyDawah = async (page) => {
    setLoading(true);
    try {
      const response = await apiClient.get("/api/FacultyDawah", {
        params: { pageNumber: page, pageSize: PAGE_SIZE },
      });
      const data = response.data;
      if (Array.isArray(data)) {
        setPosts((prevPosts) => [...prevPosts, ...data]);
        if (data.length < PAGE_SIZE) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching Faculty Dawah data:", error);
      setHasMore(false);
    }
    setLoading(false);
  };

  const onLoadMoreClick = () => {
    if (!loading && hasMore) {
      setPageNumber((prevPage) => prevPage + 1);
    }
  };

  return (
    <Container>
      <ContentWithPaddingXl>
        <HeadingRow>
          <Heading>{headingText}</Heading>
        </HeadingRow>
        <Posts>
          {posts.map((post, index) => (
           
            
            <PostContainer key={index} featured={post.featured}>
              <Post className="group">
                <Image imageSrc={getAbsoluteUrl(post.ImageUrl)} />

                <Info>
                  <Category>
                    <WordSpacedText>{post.name || "Untitled"}</WordSpacedText>
                  </Category>
                  <CreationDate>
                    {post.eventDate
                      ? new Date(post.eventDate).toLocaleDateString()
                      : "No date"}
                  </CreationDate>
                  <Title>
                    <WordSpacedText>
                      {post.description || "No Description"}
                    </WordSpacedText>
                  </Title>
                </Info>
              </Post>
            </PostContainer>
          ))}
        </Posts>
        {hasMore && (
          <ButtonContainer>
            <LoadMoreButton onClick={onLoadMoreClick} disabled={loading}>
              {loading ? "Loading..." : "Load More"}
            </LoadMoreButton>
          </ButtonContainer>
        )}
      </ContentWithPaddingXl>
    </Container>
  );
}
